import React, { useEffect } from 'react';
import Quantity from '../../Quantity/Quantity';
import _ from 'lodash';
import { useInjection } from '../../../../../../dependancyInjection/DependencyContext';
import DependencyType from '../../../../../../dependancyInjection/DependencyType';
import useStateRef from 'react-usestateref';
import { useTranslation } from 'react-i18next';
import Button, { ButtonSize, ButtonVariant } from '../../../../../shared/inputs/Button/Button';
import {
    getVariantAvailability,
    ProductVariantAvailability,
} from '../../../../../../services/ProductServices/ProductVariantAvailability';
import { usePurchase } from '../../../../../../hooks/contexts/Purchase/Purchase';
import { BasketItem } from '../../../../../../services/BasketService/Basket.type';
import { ConfigurationService } from '../../../../../../services/ConfigurationService/ConfigurationService';
import { BasketService } from '../../../../../../services/BasketService/BasketService';
import { LocalProduct, LocalProductVariant } from '../../../../../../services/ProductServices/LocalProduct';
import { KeyValuePair } from '../../../../../../provider/cloudshelf/graphql/generated/cloudshelf_types';
import { ToastService } from '../../../../../../services/ToastService/ToastService';
import { FunctionalComponentWithChildren } from '../../../../../../FCWithChildren';

export interface ProductCartOptionsProps {
    className?: string;
    product: LocalProduct;
    selectedVariant?: LocalProductVariant;
    allVariantsUnavailable: boolean;
    scroll: (to: 'variants' | 'customiser' | 'images') => void;
    formValid: boolean;
    customOptions: KeyValuePair[];
    productCustomiserPriceModifier: number;
    onCheckoutPressed?: () => void;
    maxQuantity?: number;
    onAddedToBasket?: () => void;
}

const ProductCartOptions: FunctionalComponentWithChildren<ProductCartOptionsProps> = props => {
    const { t } = useTranslation();
    const purchaseContext = usePurchase();
    const configService = useInjection<ConfigurationService>(DependencyType.ConfigurationService);
    const basketService = useInjection<BasketService>(DependencyType.BasketService);
    const [loading, setLoading] = React.useState(false);
    const [, setQuantity, quantityRef] = useStateRef(1);
    const [showAddedLabel, setShowAddedLabel] = React.useState(false);
    const toastService = useInjection<ToastService>(DependencyType.ToastService);

    useEffect(() => {
        const maxAllowed = props.maxQuantity ?? Number.MAX_VALUE;
        if (quantityRef.current > maxAllowed) {
            setQuantity(maxAllowed);
        }
    }, [props.maxQuantity]);

    useEffect(() => {
        if (props.selectedVariant) {
            if (quantityRef.current !== 1) {
                setQuantity(1);
            }
        }
    }, [props.selectedVariant]);

    useEffect(() => {
        //subscribe to basket changes
        const sub = basketService.observeBasket().subscribe(basket => {
            if (basket && basket.lineItems) {
                const lineItem = _.find(
                    basket?.lineItems,
                    li =>
                        li.variant.id === props.selectedVariant?.id &&
                        BasketService.attributesMatch(props.customOptions, li.attributes),
                );

                if (lineItem) {
                    if (quantityRef.current !== lineItem.quantity) {
                        setQuantity(lineItem.quantity);
                    }
                }
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [props.selectedVariant, props.customOptions]);

    const isInBasket = (lineItems: BasketItem[], customOptions: KeyValuePair[]) => {
        const ret = _.find(
            lineItems,
            li =>
                li.variant.id === props.selectedVariant?.id &&
                BasketService.attributesMatch(customOptions, li.attributes),
        );
        return ret !== undefined;
    };

    //Yes this is needed
    const inBasket = isInBasket(basketService.basket?.lineItems ?? [], props.customOptions);
    let buttonLabel = t('product_view.buy_buttons.add_to_basket');
    if (showAddedLabel) {
        buttonLabel = '✓ ' + t('product_view.buy_buttons.added');
    }
    // else if (inBasket) {
    //     buttonLabel = t('product_view.buy_buttons.checkout');
    // }

    const handleQuantityChange = async (quantity: number) => {
        // const isInBasket = _.some(
        //     basketService.basket?.lineItems,
        //     li => li.productVariant.id === props.selectedVariant?.id,
        // );
        const inBasket = isInBasket(basketService.basket?.lineItems ?? [], props.customOptions);
        if (inBasket && props.selectedVariant) {
            setLoading(true);
            await basketService.setItemQuantity(
                props.product,
                props.selectedVariant,
                quantity,
                props.customOptions,
                undefined,
                props.maxQuantity,
            );
            await handleProductCustomiserPriceModifier(
                props.product,
                props.selectedVariant,
                quantity,
                props.customOptions,
            );

            setLoading(false);
        } else {
            setQuantity(quantity);
        }
    };

    const handleProductCustomiserPriceModifier = async (
        realProductDetails: LocalProduct | undefined,
        realItemVariant: LocalProductVariant,
        quantity = 1,
        options: KeyValuePair[] = [],
    ) => {
        const itemCustomiserProduct = configService.productCustomiserPriceModifierProduct;
        const itemCustomiserVariant = configService.productCustomiserPriceModifierVariant;
        if (props.productCustomiserPriceModifier <= 0 || !itemCustomiserVariant || !itemCustomiserProduct) {
            return;
        }

        let customisationFor = realItemVariant.displayName;
        if (props.product) {
            customisationFor = `${props.product.title} (${realItemVariant.displayName})`;
        }

        customisationFor = `Customisation for ${customisationFor}`;
        await basketService.setItemQuantity(
            itemCustomiserProduct,
            itemCustomiserVariant,
            props.productCustomiserPriceModifier * quantity,
            options,
            customisationFor,
            props.maxQuantity,
        );
    };

    const handleAddToBasket = async () => {
        props.onCheckoutPressed?.();

        if (!props.selectedVariant) {
            props.scroll('variants');
            return;
        }

        if (!props.formValid) {
            props.scroll('customiser');
            return;
        }

        toastService.showPromiseGlass(
            new Promise(async (resolve, reject) => {
                if (!props.selectedVariant) {
                    reject();
                    return;
                }

                setLoading(true);
                const existingQuantity = await basketService.getItemQuantity(
                    props.selectedVariant.id,
                    props.customOptions,
                );
                try {
                    await basketService.setItemQuantity(
                        props.product,
                        props.selectedVariant,
                        existingQuantity + 1,
                        props.customOptions,
                        props.product?.title ?? props.selectedVariant.displayName,
                        props.maxQuantity,
                    );
                    await handleProductCustomiserPriceModifier(
                        props.product,
                        props.selectedVariant,
                        existingQuantity + 1,
                        props.customOptions,
                    );
                    props.onAddedToBasket?.();
                    resolve(true);
                    // resolve(true);
                    // setShowAddedLabel(true);
                    // setTimeout(() => setShowAddedLabel(false), 2000);
                } catch (e) {
                    reject(e);
                } finally {
                    setLoading(false);
                }
            }),
        );
    };

    const readyToAddToBasket = !!props.selectedVariant && props.formValid;

    const buttonDisabled =
        props.allVariantsUnavailable ||
        !props.selectedVariant ||
        getVariantAvailability(props.selectedVariant) === ProductVariantAvailability.Unavailable;

    return (
        <div className={props.className}>
            <div className={'ProductCardOptions'}>
                {/* <Quantity
                    initialQuantity={quantityRef.current}
                    className={'ProductCardOptions__quantity'}
                    onQuantityChanged={handleQuantityChange}
                    loading={loading}
                    variant={inBasket ? 'numInBasket' : 'inline'}
                    overrideQuantity={quantityRef.current}
                    maxQuantity={props.maxQuantity}
                />
                <div className={'ProductCardOptions__spacer'} /> */}
                <Button
                    className={
                        readyToAddToBasket
                            ? 'ProductCardOptions__buttonBuyNow'
                            : 'ProductCardOptions__buttonAddToBasket'
                    }
                    text={buttonLabel}
                    size={ButtonSize.MD}
                    onClick={handleAddToBasket}
                    disabled={buttonDisabled}
                    variant={ButtonVariant.GREENFLAT}
                    loading={loading}
                    showLoader
                    useTransition
                    translate
                    useContrastColor
                />
            </div>
        </div>
    );
};

export default ProductCartOptions;
